import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { Radio } from 'components/UI/V3/Forms/Radio/Radio';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import { useTimeZone } from 'hooks';
import moment, { Moment } from 'moment-timezone';
import React from 'react';

import styles from './SendTimePanel.module.scss';

type TimeOption = {
    id: string;
    label: string;
};

interface SendTimePanelProps {
    sendTime: 'now' | 'scheduled';
    /** ISO `yyyy-mm-dd`. */
    scheduledDate: string | null;
    /** 24-hour `HH:mm`. */
    scheduledTime: string | null;
    onChangeSendTime: (sendTime: 'now' | 'scheduled') => void;
    onChangeScheduledDate: (date: string | null) => void;
    onChangeScheduledTime: (time: string | null) => void;
}

/**
 * Left panel of Step 3 — picks WHEN to send.
 *
 *   ● Send Now            → end-to-end real (Adyen + complete on Confirm).
 *   ○ Schedule            → reveals DatePicker + TimeSelector. UI-only mock
 *                           today (BE Ticket F pending). Date+time live in
 *                           wizard memory; never persisted to BE.
 *
 * Time options are 15-min increments across 24h, generated in the marketplace
 * timezone so labels match the user's local clock. Internally we serialise
 * date as ISO `yyyy-mm-dd` and time as `HH:mm` (24h) so they round-trip
 * predictably through the wizard form values.
 */
/**
 * "1 hour from now, rounded up to the next 15-min slot" — used to populate
 * the date+time defaults the first time the user picks Schedule. The buffer
 * guarantees `isFutureScheduledDateTime` passes immediately so the header
 * Continue/Schedule button stays enabled without the user touching the
 * pickers. Honours the marketplace timezone so the displayed labels match
 * the local clock.
 */
const computeDefaultScheduledDateTime = (timeZone: string): { date: string; time: string } => {
    const target = moment.tz(timeZone).add(1, 'hour').seconds(0).milliseconds(0);
    const remainder = target.minute() % 15;
    if (remainder !== 0) target.add(15 - remainder, 'minutes');
    return {
        date: target.format('YYYY-MM-DD'),
        time: target.format('HH:mm')
    };
};

const SendTimePanel: React.FC<SendTimePanelProps> = ({
    sendTime,
    scheduledDate,
    scheduledTime,
    onChangeSendTime,
    onChangeScheduledDate,
    onChangeScheduledTime
}) => {
    const timeZone = useTimeZone();
    const [isDateSelectorExpanded, setIsDateSelectorExpanded] = React.useState(false);

    // 24h × 4 = 96 buckets. id stays in 24h `HH:mm` (matches our form values),
    // label is the friendly 12h variant ("6:15pm") the design shows.
    const timeOptions: TimeOption[] = React.useMemo(() => {
        const base = moment.tz(timeZone).startOf('day');
        return Array.from({ length: 96 }, (_, i) => {
            const t = base.clone().add(i * 15, 'minutes');
            return { id: t.format('HH:mm'), label: t.format('h:mma') };
        });
    }, [timeZone]);

    const selectedTimeOption = React.useMemo<TimeOption | undefined>(() => {
        if (!scheduledTime) return undefined;
        return timeOptions.find(option => option.id === scheduledTime);
    }, [scheduledTime, timeOptions]);

    const selectedDateMoment = React.useMemo<Moment | null>(() => {
        if (!scheduledDate) return null;
        const parsed = moment.tz(scheduledDate, 'YYYY-MM-DD', timeZone);
        return parsed.isValid() ? parsed : null;
    }, [scheduledDate, timeZone]);

    // Populate sane defaults the first time the user switches to Schedule with
    // empty date/time. Skip when either is already set so resuming a draft (or
    // toggling back from Send Now after a manual pick) doesn't clobber the
    // user's selection.
    React.useEffect(() => {
        if (sendTime !== 'scheduled') return;
        if (scheduledDate && scheduledTime) return;
        const defaults = computeDefaultScheduledDateTime(timeZone);
        if (!scheduledDate) onChangeScheduledDate(defaults.date);
        if (!scheduledTime) onChangeScheduledTime(defaults.time);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sendTime]);

    const handleDateChange = (next: Moment | null) => {
        onChangeScheduledDate(next ? next.format('YYYY-MM-DD') : null);
        setIsDateSelectorExpanded(false);
    };

    return (
        <div className={styles.root}>
            <h3 className={styles.heading}>Send Time</h3>

            <div className={styles.radios}>
                <Radio
                    checked={sendTime === 'now'}
                    inlineLabel="Send Now"
                    onChange={selected => selected && onChangeSendTime('now')}
                />
                {/*
                 * The "Schedule" radio and its date+time fields below are hidden
                 * because scheduled sending is not part of MVP scope (see CUD-3651
                 * "Out of scope: Scheduling"). The controls are preserved in source
                 * — copy, defaults, pickers — so we can re-enable everything by
                 * uncommenting when the backend ships wait_until / cron support.
                 * Keeping them hidden during QA avoids users hitting a mock flow
                 * that reports success without actually scheduling anything.
                 */}
                {/*
                <Radio
                    checked={sendTime === 'scheduled'}
                    inlineLabel="Schedule"
                    onChange={selected => selected && onChangeSendTime('scheduled')}
                />
                */}
            </div>

            {/*
            {sendTime === 'scheduled' && (
                <div className={styles.scheduleFields}>
                    <ExpandableSelector
                        id="email-schedule-date-selector"
                        title="Delivery Date"
                        expanded={isDateSelectorExpanded}
                        label={selectedDateMoment ? selectedDateMoment.format('dddd, DD MMM YYYY') : 'Select date'}
                        onExpand={() => setIsDateSelectorExpanded(prev => !prev)}
                        onClickAway={() => setIsDateSelectorExpanded(false)}
                    >
                        <DatePicker
                            timeZone={timeZone}
                            value={selectedDateMoment}
                            minDate={moment.tz(timeZone).startOf('day')}
                            onChange={handleDateChange}
                        />
                    </ExpandableSelector>

                    <TimeSelector
                        label="Time"
                        options={timeOptions}
                        value={selectedTimeOption}
                        onChange={id => onChangeScheduledTime(id)}
                    />
                </div>
            )}
            */}
        </div>
    );
};

export default SendTimePanel;
